import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ouvrirUseCase } from '../../utils/openUseCase';
import { 
  Palette, 
  FileText, 
  Image, 
  Video, 
  Presentation, 
  Users, 
  Plus, 
  Search,
  Download,
  Share2,
  Eye,
  Edit,
  Trash2,
  Star,
  Clock,
  TrendingUp,
  Mail, 
  MessageSquare, 
  Calendar, 
  Newspaper,
  FileCheck,
  Briefcase,
  Award,
  Globe,
  BookOpen
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import CountUp from '../../components/CountUp';
import { contentService } from '../../services/api';
import { ContentProject, DesignTemplate, StoryTemplate } from '../../services/api/content';
import { settingsService } from '../../services/api/settings';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { formatUseCaseCategoryLabel } from '../../utils/useCaseCategories';
import '../../styles/background-effects.css';

const iconMap: { [key: string]: React.ElementType } = {
  FileText,
  Mail,
  MessageSquare,
  Calendar,
  Presentation,
  Newspaper,
  FileCheck,
  Briefcase,
  Award,
  Globe,
  BookOpen,
  Plus,
  Palette,
  Image,
  Video
};


const ContentCreationPage: React.FC = () => {
  const navigate = useNavigate();
  const [useCases, setUseCases] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const USECASE_CONTEXT = 'support';

  const loadData = async () => {
    setLoading(true);
    try {
      const [useCasesData, categoriesData] = await Promise.all([
        settingsService.getUseCases({ context: USECASE_CONTEXT, is_active: true }),
        settingsService.getUseCaseCategories({ context: USECASE_CONTEXT, is_active: true }),
      ]);
      
      // Transformer les use cases en templates pour l'affichage
      const transformedUseCases = useCasesData.map((uc: any) => ({
        ...uc,
        color: uc.category_color || getColorFromCategory(uc.category_code || uc.category)
      }));
      setUseCases(transformedUseCases);
      setCategories(
        Array.isArray(categoriesData)
          ? categoriesData.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          : []
      );
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour mapper les catégories aux couleurs
  const getColorFromCategory = (category: string): string => {
    const categoryColorMap: { [key: string]: string } = {
      redaction: 'blue',
      infographie: 'purple',
      visuel: 'teal',
      presentation: 'red',
      video: 'orange',
      audio: 'cyan'
    };
    return categoryColorMap[category] || 'slate';
  };

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: { bg: string; border: string; text: string; hover: string } } = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-100 hover:border-blue-300'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-600',
        hover: 'hover:bg-green-100 hover:border-green-300'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-100 hover:border-purple-300'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-600',
        hover: 'hover:bg-orange-100 hover:border-orange-300'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-600',
        hover: 'hover:bg-red-100 hover:border-red-300'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-600',
        hover: 'hover:bg-teal-100 hover:border-teal-300'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-600',
        hover: 'hover:bg-indigo-100 hover:border-indigo-300'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        text: 'text-pink-600',
        hover: 'hover:bg-pink-100 hover:border-pink-300'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-600',
        hover: 'hover:bg-amber-100 hover:border-amber-300'
      },
      cyan: {
        bg: 'bg-cyan-50',
        border: 'border-cyan-200',
        text: 'text-cyan-600',
        hover: 'hover:bg-cyan-100 hover:border-cyan-300'
      },
      slate: {
        bg: 'bg-slate-50',
        border: 'border-slate-200',
        text: 'text-slate-600',
        hover: 'hover:bg-slate-100 hover:border-slate-300'
      }
    };
    return colorMap[color] || colorMap.slate;
  };

  // Filtrer les templates basés sur la recherche et la catégorie
  const filteredUseCases = useMemo(() => {
    let filtered = useCases;

    // Filtrer par catégorie
    if (selectedCategory) {
      filtered = filtered.filter(t => (t.category_code || t.category) === selectedCategory);
    }

    // Filtrer par recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(query) || 
        t.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [useCases, selectedCategory, searchQuery]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    useCases.forEach((t) => {
      const code = t.category_code || t.category;
      if (code) {
        counts[code] = (counts[code] || 0) + 1;
      }
    });
    return counts;
  }, [useCases]);

  const displayCategories = useMemo(() => {
    return categories.map((category: any) => ({
      code: category.code,
      label: category.name || category.label || formatUseCaseCategoryLabel(category.code),
      count: categoryCounts[category.code] || 0,
    }));
  }, [categories, categoryCounts]);

  return (
    <DashboardLayout>
      {/* Aurora Background Effect */}
      
      <div className="space-y-6 content-above-aurora">
        {/* En-tête */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Création de Contenu</h1>
          <p className="text-gray-600">Conception de visuels, présentations et contenus percutants</p>
        </div>

        {/* Templates */}
          <div className="space-y-6">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <>
                {/* Section de recherche et filtres */}
                <div className="space-y-4">
                  {/* Barre de recherche */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher un modèle..."
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Filtres par catégorie */}
                  {displayCategories.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedCategory(null)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          selectedCategory === null
                            ? 'bg-teal-dark text-white shadow-sm'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Tous ({useCases.length})
                      </button>
                      {displayCategories.map((category) => (
                        <button
                          key={category.code}
                          onClick={() =>
                            setSelectedCategory(category.code === selectedCategory ? null : category.code)
                          }
                          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                            selectedCategory === category.code
                              ? 'bg-teal-dark text-white shadow-sm'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {category.label} ({category.count})
                          <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/30 text-xs font-semibold text-white">
                            {category.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Résultats */}
                {filteredUseCases.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <Search className="h-16 w-16 mx-auto" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Aucun résultat trouvé
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {searchQuery ? `Aucun modèle ne correspond à "${searchQuery}"` : "Aucun modèle dans cette catégorie"}
                    </p>
                    {(searchQuery || selectedCategory) && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory(null);
                        }}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Réinitialiser les filtres
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredUseCases.map((template, index) => {
                      const colors = getColorClasses(template.color);
                      const Icon = template.icon_name && iconMap[template.icon_name] 
                        ? iconMap[template.icon_name] 
                        : FileText;
                      
                      return (
                        <motion.div
                          key={template.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          onClick={() => ouvrirUseCase(template, navigate)}
                        >
                          <Card 
                            className={`${colors.bg} ${colors.border} border-2 transition-all duration-200 ${colors.hover} cursor-pointer`}
                            padding="none"
                          >
                              {/* Image ou icône par défaut */}
                              {(template.image_url || template.image) ? (
                                <div className="relative w-full h-48 overflow-hidden rounded-t-lg">
                                  <img 
                                    src={template.image_url || template.image} 
                                    alt={template.title}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ) : (
                                <div className={`w-full h-48 ${colors.bg} flex items-center justify-center rounded-t-lg border-b-2 ${colors.border}`}>
                                  <Icon className={`w-16 h-16 ${colors.text} opacity-50`} />
                                </div>
                              )}
                              
                              {/* Contenu du card */}
                              <div className="p-6">
                                <h3 className={`${colors.text} text-lg font-semibold mb-2`}>
                                  {template.title}
                                </h3>
                                <p className="text-gray-600 text-sm">
                                  {template.summary || template.description}
                                </p>
                              </div>
                            </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
      </div>
    </DashboardLayout>
  );
};

export default ContentCreationPage;
